import {unwrapResult} from '@reduxjs/toolkit';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {METHOD_KEYS, HAIR_LOSS_TELECONSULTATION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {Option, Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire8Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();

  const {loading} = useRequestState(addQuestionnaire);
  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[HAIR_LOSS_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]?.data,
  );
  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const {wpAuthUser} = medicalQuestionnaire;
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === HAIR_LOSS_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
  );

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          maximum: 1000,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tooLong: '^This field is too long (maximum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };

  const {inputs, setInputs, trimSpaces, validation} = useInput(
    {
      selectedOptions: currentAnswer?.selectedOptions || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
      relations: currentAnswer?.relations || [],
    },
    {rules},
  );

  const onClickOption = (answer: {value: string | number; label: string; relations?: Relation[]}) => {
    const {value} = answer;
    setInputs({selectedOptions: value, medicationsTaking: ''});
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOptions === 'YES') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }

    const questionnaireAnswer = {
      key: HAIR_LOSS_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
      index: questionnaire?.index || '',
      data: {
        selectedOptions: inputs.selectedOptions || '',
        medicationsTaking: inputs.medicationsTaking.trim() || '',
        relations: inputs.relations,
      },
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [HAIR_LOSS_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.HAIR_LOSS_TELECONSULTATION,
        },
      }),
    );

    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(
          `/appointment?type=${type}&methodKey=${METHOD_KEYS.HAIR_LOSS_TELECONSULTATION}&token=${savedToken}`,
        );
      }
    }
  };

  const renderOptionRelations = () => {
    if (inputs.selectedOptions === 'YES') {
      const selectedOptionRelations = questionnaire?.options?.find(i => i.value === inputs.selectedOptions);
      return (
        <>
          {selectedOptionRelations?.relations?.map(relation => {
            if (relation.elementType?.name === 'inputArea') {
              return (
                <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft" w="100%">
                  <UiText fontSize="md" fontWeight="bold">
                    {relation.questions.default}
                  </UiText>
                  <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
                    <UiTextarea
                      mt={spacing.small}
                      minH="300px"
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={relation.elementType.placeholder}
                      defaultValue={inputs.medicationsTaking}
                      onChange={({target}) => setInputs({medicationsTaking: target.value})}
                      maxLength={1000}
                    />
                    {validation?.errors?.medicationsTaking && (
                      <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
          })}
        </>
      );
    } else {
      return (
        <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft" w="100%">
          {questionnaire?.descriptions?.default?.map(val => (
            <UiText key={val} mt={spacing.small} textColor="liteOuter">
              {val}
            </UiText>
          ))}
        </UiBox>
      );
    }
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOptions}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOptions === value.value ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderOptionRelations()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire8Screen;
